import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Trash2,
  Wrench,
  Download,
  Eye,
  Globe2,
  Image as ImageIcon,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ShieldCheck,
  Loader2,
  Link2,
  FileText,
  Sparkles,
  Cloud,
  Zap,
  Network,
  Plus,
  Copy,
  FileUp,
  Settings,
  Save,
  Palette,
  Star,
  RefreshCw,
} from "lucide-react";
import { API } from "./Sidebar";

export const ProjectDetailPage = () => {
  const location = useLocation();
  const projectId = location.pathname.split('/').pop();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewPage, setPreviewPage] = useState("home");
  const [previewSlug, setPreviewSlug] = useState("");
  const [activeTab, setActiveTab] = useState("preview");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [wikiUrl, setWikiUrl] = useState("");
  const [wikiPreview, setWikiPreview] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [seoReport, setSeoReport] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  const [fixingSeo, setFixingSeo] = useState(false);
  const [wpUrl, setWpUrl] = useState("");
  const [wpUser, setWpUser] = useState("");
  const [wpPass, setWpPass] = useState("");
  const [wpTesting, setWpTesting] = useState(false);
  const [wpConnected, setWpConnected] = useState(false);
  const [wpPublishing, setWpPublishing] = useState(false);
  const [wpResult, setWpResult] = useState(null);
  const [wpPagesList, setWpPagesList] = useState([]);
  const [wpLockedSlugs, setWpLockedSlugs] = useState(new Set());
  const [wpPagesLoading, setWpPagesLoading] = useState(false);
  const [wpLocksSaving, setWpLocksSaving] = useState(false);
  const [bunnyZone, setBunnyZone] = useState("");
  const [bunnyKey, setBunnyKey] = useState("");
  const [bunnyPath, setBunnyPath] = useState("");
  const [bunnyRegion, setBunnyRegion] = useState("default");
  const [bunnyPullUrl, setBunnyPullUrl] = useState("");
  const [bunnyTesting, setBunnyTesting] = useState(false);
  const [bunnyConnected, setBunnyConnected] = useState(false);
  const [bunnyDeploying, setBunnyDeploying] = useState(false);
  const [bunnyResult, setBunnyResult] = useState(null);
  const [cfApiToken, setCfApiToken] = useState("");
  const [cfAccountId, setCfAccountId] = useState("");
  const [cfTesting, setCfTesting] = useState(false);
  const [cfConnected, setCfConnected] = useState(false);
  const [cfDeploying, setCfDeploying] = useState(false);
  const [cfResult, setCfResult] = useState(null);
  const [cfDomain, setCfDomain] = useState("");
  const [cfDomainAdding, setCfDomainAdding] = useState(false);
  const [cfDomainResult, setCfDomainResult] = useState(null);
  const [blogTopic, setBlogTopic] = useState("");
  const [blogGenerating, setBlogGenerating] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [suggestingTopics, setSuggestingTopics] = useState(false);
  const [blogPublishDate, setBlogPublishDate] = useState("");
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [satellites, setSatellites] = useState([]);

  // Bulk Clone state
  const [csvFile, setCsvFile] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);
  const [bulkAiJobId, setBulkAiJobId] = useState(null);
  const [bulkAiProgress, setBulkAiProgress] = useState(null);
  const [bulkDeployJobId, setBulkDeployJobId] = useState(null);
  const [deployPlatform, setDeployPlatform] = useState("cloudflare");
  const [deployCreds, setDeployCreds] = useState({ api_token: "", account_id: "", storage_zone: "", access_key: "", pull_zone_url: "", region: "default" });
  const [bulkDeploying, setBulkDeploying] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [satCreating, setSatCreating] = useState(false);
  const [satAngle, setSatAngle] = useState("tips_blog");
  const [satName, setSatName] = useState("");
  const [satTargetUrl, setSatTargetUrl] = useState("");
  const [satShowForm, setSatShowForm] = useState(false);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`);
      setProject(res.data);
      if (res.data.wp_credentials) {
        setWpUrl(res.data.wp_credentials.site_url || "");
        setWpUser(res.data.wp_credentials.username || "");
        setWpPass(res.data.wp_credentials.app_password || "");
        setWpConnected(true);
      }
      if (res.data.wp_publish_result) {
        setWpResult(res.data.wp_publish_result);
      }
      if (Array.isArray(res.data.wp_locked_pages)) {
        setWpLockedSlugs(new Set(res.data.wp_locked_pages));
      }
      if (res.data.bunny_credentials) {
        setBunnyZone(res.data.bunny_credentials.storage_zone || "");
        setBunnyKey(res.data.bunny_credentials.access_key || "");
        setBunnyPath(res.data.bunny_credentials.remote_path || "");
        setBunnyRegion(res.data.bunny_credentials.region || "default");
        setBunnyPullUrl(res.data.bunny_credentials.pull_zone_url || "");
        setBunnyConnected(true);
      }
      if (res.data.bunny_deploy_result) {
        setBunnyResult(res.data.bunny_deploy_result);
      }
      if (res.data.cf_credentials) {
        setCfApiToken(res.data.cf_credentials.api_token || "");
        setCfAccountId(res.data.cf_credentials.account_id || "");
        setCfConnected(true);
      }
      if (res.data.cf_deploy_result) {
        setCfResult(res.data.cf_deploy_result);
      }
      if (res.data.cf_custom_domain) {
        setCfDomain(res.data.cf_custom_domain);
      }
      // Fetch satellites
      try {
        const satRes = await axios.get(`${API}/projects/${projectId}/satellites`);
        setSatellites(satRes.data || []);
      } catch {}
    } catch (e) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchWikiPreview = async () => {
    if (!wikiUrl) return;
    try {
      const res = await axios.post(`${API}/projects/${projectId}/ai/fetch-wiki?wiki_url=${encodeURIComponent(wikiUrl)}`);
      setWikiPreview(res.data.content);
    } catch (e) {
      toast.error("Failed to fetch Wikipedia content");
    }
  };

  const generateServiceContent = async (service) => {
    setAiGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/ai/generate-service-content`, {
        service_name: service.name, service_slug: service.slug, wiki_url: wikiUrl || null
      });
      toast.success(`Generated content for ${service.name}`);
      await fetchProject();
      setWikiUrl(""); setWikiPreview("");
    } catch (e) { toast.error("Failed to generate content"); }
    finally { setAiGenerating(false); }
  };

  const generateHomepageContent = async () => {
    setAiGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/ai/generate-homepage-content`, {}, { timeout: 120000 });
      toast.success("Homepage content generated successfully");
      await fetchProject();
    } catch (e) { toast.error("Failed to generate homepage content"); }
    finally { setAiGenerating(false); }
  };

  const generateCityContent = async (city) => {
    setAiGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/ai/generate-city-content`, {
        city_name: city.name, city_slug: city.slug, state: city.state, wiki_url: wikiUrl || null
      });
      toast.success(`Generated content for ${city.name}`);
      await fetchProject();
      setWikiUrl(""); setWikiPreview("");
    } catch (e) { toast.error("Failed to generate content"); }
    finally { setAiGenerating(false); }
  };

  const generateCityServiceContent = async (city, service) => {
    setAiGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/ai/generate-city-service-content`, {
        city_name: city.name, city_slug: city.slug, state: city.state,
        service_name: service.name, service_slug: service.slug, wiki_url: wikiUrl || null
      });
      toast.success(`Generated content for ${service.name} in ${city.name}`);
      await fetchProject();
      setWikiUrl(""); setWikiPreview("");
    } catch (e) { toast.error("Failed to generate content"); }
    finally { setAiGenerating(false); }
  };

  // ---- Bulk generators (iterate sequentially to avoid rate-limiting) -------
  // Track the in-flight bulk job (e.g. "service:3/8") so we can show progress
  // on the right "Generate All" button while disabling the others.
  const [bulkSinglePageProgress, setBulkSinglePageProgress] = useState(null);

  const _bulkGenerate = async (kind, items, runOne) => {
    if (!items?.length) return;
    setAiGenerating(true);
    setBulkSinglePageProgress({ kind, current: 0, total: items.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < items.length; i++) {
      setBulkSinglePageProgress({ kind, current: i + 1, total: items.length, errors });
      try {
        await runOne(items[i]);
      } catch (e) {
        errors += 1;
      }
    }
    await fetchProject();
    setWikiUrl(""); setWikiPreview("");
    setBulkSinglePageProgress(null);
    setAiGenerating(false);
    if (errors) {
      toast.error(`Generated ${items.length - errors}/${items.length} ${kind} pages — ${errors} failed`);
    } else {
      toast.success(`Generated AI content for all ${items.length} ${kind} pages`);
    }
  };

  const generateAllServiceContent = () => {
    const todo = (project?.services || []).filter((s) => !s.ai_content);
    const skipped = (project?.services?.length || 0) - todo.length;
    if (!todo.length) {
      toast.info("All service pages already have AI content — nothing to generate");
      return;
    }
    if (skipped > 0) toast.info(`Skipping ${skipped} already-generated service page${skipped > 1 ? "s" : ""}`);
    return _bulkGenerate("service", todo, (service) =>
      axios.post(`${API}/projects/${projectId}/ai/generate-service-content`, {
        service_name: service.name, service_slug: service.slug, wiki_url: wikiUrl || null,
      })
    );
  };

  const generateAllCityContent = () => {
    const todo = (project?.cities || []).filter((c) => !c.ai_content);
    const skipped = (project?.cities?.length || 0) - todo.length;
    if (!todo.length) {
      toast.info("All city pages already have AI content — nothing to generate");
      return;
    }
    if (skipped > 0) toast.info(`Skipping ${skipped} already-generated city page${skipped > 1 ? "s" : ""}`);
    return _bulkGenerate("city", todo, (city) =>
      axios.post(`${API}/projects/${projectId}/ai/generate-city-content`, {
        city_name: city.name, city_slug: city.slug, state: city.state, wiki_url: wikiUrl || null,
      })
    );
  };

  const generateAllCityServiceContent = () => {
    const combos = [];
    const comboContent = project?.combo_content || {};
    let total = 0;
    (project?.cities || []).forEach((city) => {
      (project?.services || []).forEach((service) => {
        total += 1;
        const key = `${city.slug}-${service.slug}`;
        if (!comboContent[key]) combos.push({ city, service });
      });
    });
    const skipped = total - combos.length;
    if (!combos.length) {
      toast.info("All city + service combos already have AI content — nothing to generate");
      return;
    }
    if (skipped > 0) toast.info(`Skipping ${skipped} already-generated combo${skipped > 1 ? "s" : ""}`);
    return _bulkGenerate("combo", combos, ({ city, service }) =>
      axios.post(`${API}/projects/${projectId}/ai/generate-city-service-content`, {
        city_name: city.name, city_slug: city.slug, state: city.state,
        service_name: service.name, service_slug: service.slug, wiki_url: wikiUrl || null,
      })
    );
  };

  const generateBlogArticle = async () => {
    if (!blogTopic.trim()) return;
    setBlogGenerating(true);
    try {
      const payload = { topic: blogTopic.trim() };
      if (blogPublishDate) payload.publish_date = blogPublishDate;
      const res = await axios.post(`${API}/projects/${projectId}/ai/generate-blog-article`, payload);
      const statusLabel = blogPublishDate ? `scheduled for ${blogPublishDate}` : "published";
      toast.success(`Blog article ${statusLabel}: ${res.data.article?.title || blogTopic}`);
      setBlogTopic("");
      setBlogPublishDate("");
      await fetchProject();
    } catch (e) { toast.error("Failed to generate blog article"); }
    finally { setBlogGenerating(false); }
  };

  const deleteBlogArticle = async (slugToDelete) => {
    try {
      const updatedArticles = (project.blog_articles || []).filter(a => a.slug !== slugToDelete);
      await axios.put(`${API}/projects/${projectId}`, { blog_articles: updatedArticles });
      toast.success("Article removed");
      await fetchProject();
    } catch (e) { toast.error("Failed to remove article"); }
  };

  const publishBlogArticle = async (slug) => {
    try {
      const updatedArticles = (project.blog_articles || []).map(a =>
        a.slug === slug ? { ...a, status: "published", published: true, publish_date: new Date().toISOString().split('T')[0] } : a
      );
      await axios.put(`${API}/projects/${projectId}`, { blog_articles: updatedArticles });
      toast.success("Article published! Redeploy to make it live.");
      await fetchProject();
    } catch (e) { toast.error("Failed to publish article"); }
  };

  const suggestBlogTopics = async () => {
    setSuggestingTopics(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/ai/suggest-blog-topics`);
      setSuggestedTopics(res.data.topics || []);
    } catch (e) { toast.error("Failed to suggest topics"); }
    finally { setSuggestingTopics(false); }
  };

  const bulkGenerateContent = async () => {
    setBulkGenerating(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/ai/bulk-generate`, {}, { timeout: 600000 });
      setBulkResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
        await fetchProject();
      }
    } catch (e) { toast.error("Bulk generation failed — some content may have been generated. Refresh to check."); await fetchProject(); }
    finally { setBulkGenerating(false); }
  };

  const createSatellite = async () => {
    setSatCreating(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/satellites`, {
        name: satName.trim(),
        content_angle: satAngle,
        target_url: satTargetUrl.trim() || "",
      }, { timeout: 120000 });
      if (res.data.success) {
        toast.success(`Satellite site "${res.data.satellite.name}" created!`);
        setSatellites(prev => [...prev, res.data.satellite]);
        setSatName(""); setSatShowForm(false);
      }
    } catch (e) { toast.error("Failed to create satellite site"); }
    finally { setSatCreating(false); }
  };

  const deleteSatellite = async (satId) => {
    if (!window.confirm("Delete this satellite site?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/satellites/${satId}`);
      setSatellites(prev => prev.filter(s => s.id !== satId));
      toast.success("Satellite deleted");
    } catch (e) { toast.error("Failed to delete satellite"); }
  };

  const deploySatelliteCF = async (satId) => {
    const sat = satellites.find(s => s.id === satId);
    if (!sat) return;
    setSatellites(prev => prev.map(s => s.id === satId ? { ...s, _deploying: true } : s));
    try {
      const res = await axios.post(`${API}/projects/${projectId}/satellites/${satId}/deploy-cloudflare`, {}, { timeout: 180000 });
      if (res.data.success) {
        toast.success(`Deployed to ${res.data.pages_dev_url}`);
        // Refresh satellites
        const satRes = await axios.get(`${API}/projects/${projectId}/satellites`);
        setSatellites(satRes.data || []);
      } else { toast.error(res.data.error || "Deploy failed"); }
    } catch (e) { toast.error("Cloudflare deploy failed"); }
    finally { setSatellites(prev => prev.map(s => s.id === satId ? { ...s, _deploying: false } : s)); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file (PNG recommended)"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/projects/${projectId}/upload-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded and converted to WEBP");
      await fetchProject();
    } catch (err) { toast.error("Failed to upload logo"); }
    finally { setUploadingLogo(false); }
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}/logo`);
      toast.success("Logo removed");
      await fetchProject();
    } catch (err) { toast.error("Failed to remove logo"); }
  };

  if (loading) return <p className="text-center py-8">Loading...</p>;
  if (!project) return <p className="text-center py-8">Project not found</p>;

  const previewUrl = previewSlug
    ? `${API}/projects/${projectId}/preview/${previewPage}?slug=${previewSlug}`
    : `${API}/projects/${projectId}/preview/${previewPage}`;

  const logoUrl = project.has_logo ? `${API}/projects/${projectId}/logo` : null;

  return (
    <div data-testid="project-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Logo display/upload area */}
          <div className="relative group" data-testid="project-logo-area">
            {logoUrl ? (
              <div className="relative">
                <img src={logoUrl} alt={`${project.business_name} logo`} className="h-14 w-auto max-w-[180px] object-contain rounded-lg border border-slate-200 bg-white p-1.5" data-testid="project-logo-img" />
                <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <label className="cursor-pointer p-1 bg-white/90 rounded text-slate-700 hover:bg-white" title="Replace logo">
                    <Upload className="w-3.5 h-3.5" />
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" />
                  </label>
                  <button onClick={handleLogoDelete} className="p-1 bg-white/90 rounded text-red-600 hover:bg-white" title="Remove logo" data-testid="project-logo-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-14 h-14 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors" data-testid="project-logo-upload">
                <ImageIcon className="w-5 h-5 text-slate-400" />
                <span className="text-[10px] text-slate-400 mt-0.5">Logo</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" />
              </label>
            )}
            {uploadingLogo && (
              <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <Link to="/admin/projects" className="text-sm text-slate-500 hover:text-slate-700 mb-2 inline-flex items-center">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Projects
            </Link>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{project.name}</h2>
            <p className="text-slate-600">{project.business_name}</p>
          </div>
        </div>
        <Button onClick={() => window.open(`${API}/projects/${projectId}/download`, '_blank')} className="bg-orange-500 hover:bg-orange-600">
          <Download className="w-4 h-4 mr-2" /> Download ZIP
        </Button>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-4 mb-6 border-b overflow-x-auto pb-px">
        <button className={`pb-3 px-1 font-medium whitespace-nowrap ${activeTab === 'settings' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => { setActiveTab('settings'); if (!editForm && project) setEditForm({ business_name: project.business_name || '', phone: project.phone || '', email: project.email || '', address: project.address || '', city: project.city || '', state: project.state || '', zip_code: project.zip_code || '', tagline: project.tagline || '', description: project.description || '', domain_name: project.domain_name || '', primary_color: project.primary_color || '#ea580c', secondary_color: project.secondary_color || '#0f172a', whatsapp: project.whatsapp || '', google_maps_embed: project.google_maps_embed || '', mobile_sticky_cta_label: project.mobile_sticky_cta_label || '', disclaimer: project.disclaimer || '', head_scripts: project.head_scripts || '', body_scripts: project.body_scripts || '', show_hero_stats: project.show_hero_stats || false, show_trust_badges: project.show_trust_badges || false, show_service_areas: project.show_service_areas != null ? project.show_service_areas : !(project.template || '').startsWith('admin-gestion'), logo_custom_url: project.logo_custom_url || '', logo_footer_custom_url: project.logo_footer_custom_url || '', favicon_custom_url: project.favicon_custom_url || '', wp_default_thumbnail_url: project.wp_default_thumbnail_url || '', why_image_url: project.why_image_url || '', about_image_url: project.about_image_url || '', founder_photo_url: project.founder_photo_url || '', services_layout: project.services_layout || 'grid', service_images: (project.services || []).map(s => s.image_url || ''), siret: project.siret || '', legal_form: project.legal_form || '', rgpd_url: project.rgpd_url || '', phone_secondary: project.phone_secondary || '', site_role: project.site_role || 'direct_provider', network_name: project.network_name || '', network_disclaimer: project.network_disclaimer || '', service_area_business: project.service_area_business || false, service_area_suffix: project.service_area_suffix || '' }); }} data-testid="settings-tab">
          <Settings className="w-4 h-4 inline mr-2" />Settings
        </button>
        <button className={`pb-3 px-1 font-medium whitespace-nowrap ${activeTab === 'preview' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('preview')}>
          <Eye className="w-4 h-4 inline mr-2" />Preview
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'ai-content' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('ai-content')}>
          <Wrench className="w-4 h-4 inline mr-2" />AI Content Writer
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'seo-audit' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`}
          onClick={() => { setActiveTab('seo-audit'); if (!seoReport) { setSeoLoading(true); axios.get(`${API}/projects/${projectId}/seo-audit`).then(res => setSeoReport(res.data)).catch(() => toast.error("Failed to run SEO audit")).finally(() => setSeoLoading(false)); } }}
          data-testid="seo-audit-tab">
          <BarChart3 className="w-4 h-4 inline mr-2" />SEO Audit
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'wordpress' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('wordpress')} data-testid="wordpress-tab">
          <Globe2 className="w-4 h-4 inline mr-2" />WordPress
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'bunny-cdn' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('bunny-cdn')} data-testid="bunny-cdn-tab">
          <Upload className="w-4 h-4 inline mr-2" />Bunny CDN
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'cloudflare' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('cloudflare')} data-testid="cloudflare-tab">
          <Cloud className="w-4 h-4 inline mr-2" />Cloudflare Pages
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'backlinks' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('backlinks')} data-testid="backlinks-tab">
          <Network className="w-4 h-4 inline mr-2" />Backlink Network
        </button>
        <button className={`pb-3 px-1 font-medium ${activeTab === 'bulk-clone' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-slate-500'}`} onClick={() => setActiveTab('bulk-clone')} data-testid="bulk-clone-tab">
          <Copy className="w-4 h-4 inline mr-2" />Bulk Clone
        </button>
      </div>


      {/* Settings Tab */}
      {activeTab === 'settings' && editForm && (
        <div className="space-y-6" data-testid="settings-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5 text-orange-500" />Colors</CardTitle>
              <CardDescription>Change the color scheme — affects all generated pages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="color" value={editForm.primary_color} onChange={e => setEditForm(prev => ({...prev, primary_color: e.target.value}))} className="w-12 h-10 rounded border cursor-pointer" data-testid="edit-primary-color" />
                    <Input value={editForm.primary_color} onChange={e => setEditForm(prev => ({...prev, primary_color: e.target.value}))} className="font-mono text-sm" />
                  </div>
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input type="color" value={editForm.secondary_color} onChange={e => setEditForm(prev => ({...prev, secondary_color: e.target.value}))} className="w-12 h-10 rounded border cursor-pointer" data-testid="edit-secondary-color" />
                    <Input value={editForm.secondary_color} onChange={e => setEditForm(prev => ({...prev, secondary_color: e.target.value}))} className="font-mono text-sm" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Display Options</CardTitle>
              <CardDescription>Toggle sections on/off for generated pages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50" data-testid="toggle-hero-stats">
                  <input type="checkbox" checked={editForm.show_hero_stats || false} onChange={e => setEditForm(prev => ({...prev, show_hero_stats: e.target.checked}))} className="w-4 h-4 accent-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Hero Stats</p>
                    <p className="text-xs text-slate-400">Years experience, happy customers, etc.</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50" data-testid="toggle-trust-badges">
                  <input type="checkbox" checked={editForm.show_trust_badges || false} onChange={e => setEditForm(prev => ({...prev, show_trust_badges: e.target.checked}))} className="w-4 h-4 accent-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Trust Badges</p>
                    <p className="text-xs text-slate-400">Licensed, insured, satisfaction badges below hero</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 sm:col-span-2" data-testid="toggle-service-areas">
                  <input
                    type="checkbox"
                    checked={!!editForm.show_service_areas}
                    onChange={e => setEditForm(prev => ({...prev, show_service_areas: e.target.checked}))}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Service Areas menu &amp; page</p>
                    <p className="text-xs text-slate-400">Header link + footer column + dedicated <code>/service-areas.html</code> hub. Auto-disabled by default on <code>admin-gestion-fr</code> / <code>admin-gestion-premium</code> (B2B templates with no geographic relevance) — tick to force-enable.</p>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>Website Domain <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                  <Input
                    value={editForm.domain_name}
                    onChange={e => setEditForm(prev => ({...prev, domain_name: e.target.value}))}
                    placeholder="https://yourbusiness.com"
                    data-testid="edit-domain-name"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Leave empty to use <strong>relative paths</strong> — lets you double-click the exported ZIP's <code>index.html</code> locally to test before deploying. Set it to make all exported links absolute (ZIP, WordPress, Cloudflare, Bunny). <code>https://</code> is added automatically if omitted.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Logo URL override <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                  <Input
                    value={editForm.logo_custom_url}
                    onChange={e => setEditForm(prev => ({...prev, logo_custom_url: e.target.value}))}
                    placeholder="https://cdn.example.com/logo.png or https://your-wp-site.com/wp-content/uploads/2026/01/logo.png"
                    data-testid="edit-logo-custom-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    When set, this URL is used everywhere the logo appears (ZIP / WordPress / Cloudflare / Bunny). Useful for WordPress exports — upload the logo to your WP Media Library, paste its URL here, and the exported pages will reference it correctly. Leave empty to use the uploaded logo bundled with the site.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Footer logo URL override <span className="text-slate-400 text-xs font-normal">(optional — light variant for dark footer)</span></Label>
                  <Input
                    value={editForm.logo_footer_custom_url || ''}
                    onChange={e => setEditForm(prev => ({...prev, logo_footer_custom_url: e.target.value}))}
                    placeholder="https://cdn.example.com/logo-white.png"
                    data-testid="edit-logo-footer-custom-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Use this when your footer background is dark (e.g. <code>admin-gestion-fr</code> / <code>admin-gestion-premium</code> have a black footer with a white header). Paste the URL of a light/white version of your logo. Leave empty to reuse the main logo.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Default thumbnail / share image URL <span className="text-slate-400 text-xs font-normal">(optional — applies to ALL exports)</span></Label>
                  <Input
                    value={editForm.wp_default_thumbnail_url || ''}
                    onChange={e => setEditForm(prev => ({...prev, wp_default_thumbnail_url: e.target.value}))}
                    placeholder="https://cdn.example.com/og-default.jpg"
                    data-testid="edit-wp-default-thumbnail-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Single image used as the site's default share/preview thumbnail. Applied as:{" "}
                    <strong>WordPress featured image</strong> (uploaded once to your Media Library, assigned to every page),{" "}
                    <strong>Open Graph <code>og:image</code></strong> + <strong>Twitter <code>summary_large_image</code></strong> on every static HTML page (used by Facebook, LinkedIn, WhatsApp, iMessage, Slack — covers Cloudflare Pages, Bunny CDN, ZIP exports), and{" "}
                    <strong>schema.org <code>image</code></strong> in the LocalBusiness JSON-LD. Must be an absolute <code>https://</code> URL. Recommended size: 1200×630 (OG / Twitter ratio).
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Favicon URL override <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                  <Input
                    value={editForm.favicon_custom_url}
                    onChange={e => setEditForm(prev => ({...prev, favicon_custom_url: e.target.value}))}
                    placeholder="https://cdn.example.com/favicon.ico"
                    data-testid="edit-favicon-custom-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Same idea as the logo override — paste the final absolute URL of the favicon. Leave empty to use the uploaded favicon.
                  </p>
                </div>
                <div>
                  <Label>Business Name</Label>
                  <Input value={editForm.business_name} onChange={e => setEditForm(prev => ({...prev, business_name: e.target.value}))} data-testid="edit-business-name" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={editForm.phone} onChange={e => setEditForm(prev => ({...prev, phone: e.target.value}))} data-testid="edit-phone" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={editForm.email} onChange={e => setEditForm(prev => ({...prev, email: e.target.value}))} data-testid="edit-email" />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input value={editForm.whatsapp} onChange={e => setEditForm(prev => ({...prev, whatsapp: e.target.value}))} placeholder="+1234567890" data-testid="edit-whatsapp" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Input value={editForm.address} onChange={e => setEditForm(prev => ({...prev, address: e.target.value}))} data-testid="edit-address" />
                </div>
                <div>
                  <Label>City (Main Location)</Label>
                  <Input value={editForm.city} onChange={e => setEditForm(prev => ({...prev, city: e.target.value}))} data-testid="edit-city" />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={editForm.state} onChange={e => setEditForm(prev => ({...prev, state: e.target.value}))} data-testid="edit-state" />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input value={editForm.zip_code} onChange={e => setEditForm(prev => ({...prev, zip_code: e.target.value}))} data-testid="edit-zip" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Content</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div>
                  <Label>H1 suffix <span className="text-slate-400 text-xs font-normal">(optional — appended to the homepage H1 after the business name)</span></Label>
                  <Input
                    value={editForm.tagline}
                    onChange={e => setEditForm(prev => ({...prev, tagline: e.target.value}))}
                    placeholder="Pro, LLC, Experts, Inc., …"
                    data-testid="edit-tagline"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Example: business name <code>Antioch Bathroom Remodel Contractors</code> + suffix <code>Pro</code> → H1 reads <code>Antioch Bathroom Remodel Contractors Pro</code>. Leave empty to display only the business name.
                  </p>
                </div>
                <div>
                  <Label>Description</Label>
                  <textarea value={editForm.description} onChange={e => setEditForm(prev => ({...prev, description: e.target.value}))} className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm" data-testid="edit-description" />
                </div>
                <div>
                  <Label>Disclaimer</Label>
                  <textarea value={editForm.disclaimer} onChange={e => setEditForm(prev => ({...prev, disclaimer: e.target.value}))} className="w-full min-h-[60px] px-3 py-2 border rounded-md text-sm" placeholder="Optional footer disclaimer text" data-testid="edit-disclaimer" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="service-images-card">
            <CardHeader>
              <CardTitle>Service & Section Images</CardTitle>
              <CardDescription>
                Override the default Unsplash images used on the homepage services grid, the service detail pages, and the "Why Choose Us" section. Paste any absolute <code>https://</code> URL. Leave a field empty to keep the auto-generated image.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                <div>
                  <Label>"Why Choose Us" image URL <span className="text-slate-400 text-xs font-normal">(optional — replaces the .why-image-wrapper image)</span></Label>
                  <Input
                    value={editForm.why_image_url || ''}
                    onChange={e => setEditForm(prev => ({...prev, why_image_url: e.target.value}))}
                    placeholder="https://cdn.example.com/why-choose-us.jpg"
                    data-testid="edit-why-image-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used in the homepage's "Why Choose Us" section (CSS class <code>.why-image-wrapper</code>). Recommended ratio: 4:3 or 3:2 (e.g. 800×600 or 1200×800).
                  </p>
                </div>

                <div>
                  <Label>About page image URL <span className="text-slate-400 text-xs font-normal">(optional — replaces the .about-image image)</span></Label>
                  <Input
                    value={editForm.about_image_url || ''}
                    onChange={e => setEditForm(prev => ({...prev, about_image_url: e.target.value}))}
                    placeholder="https://cdn.example.com/about-team.jpg"
                    data-testid="edit-about-image-url"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used on the About page next to the "Our Story / Our Values" block (CSS class <code>.about-image</code>). Recommended ratio: 3:2 or 4:3 (e.g. 1200×800).
                  </p>
                </div>

                <div>
                  <Label>Homepage "Our Services" layout</Label>
                  <select
                    value={editForm.services_layout || 'grid'}
                    onChange={e => setEditForm(prev => ({...prev, services_layout: e.target.value}))}
                    className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                    data-testid="edit-services-layout"
                  >
                    <option value="grid">Grid — default 2-column card grid</option>
                    <option value="bento">Bento — 1 hero card + smaller cards (modern)</option>
                    <option value="zigzag">Zig-zag — alternating image/text rows (editorial)</option>
                    <option value="numbered">Numbered list — Swiss-style 01. 02. 03. (minimal)</option>
                    <option value="diagonal-cut">Diagonal cut — clipped-corner cards (industrial)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Pure-CSS variants — no JS, no rebuild. Differentiates clones visually without changing content. Use <strong>Bento</strong> for premium niches, <strong>Zig-zag</strong> for editorial/luxury, <strong>Numbered</strong> for legal/consulting, <strong>Diagonal cut</strong> for blue-collar trades.
                  </p>
                </div>

                {(project?.template === 'malias-consulting') && (
                  <div>
                    <Label>Founder photo URL <span className="text-slate-400 text-xs font-normal">(optional — for the About page founder section)</span></Label>
                    <Input
                      value={editForm.founder_photo_url || ''}
                      onChange={e => setEditForm(prev => ({...prev, founder_photo_url: e.target.value}))}
                      placeholder="https://cdn.example.com/michele-sock.jpg"
                      data-testid="edit-founder-photo-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Portrait of the founder rendered in the About page "Notre fondatrice" section. Square or 4:5 portrait orientation works best (e.g. 800×1000).
                    </p>
                  </div>
                )}

                <div>
                  <Label>Per-service images <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Each URL is used both on the homepage services grid and on the corresponding service detail page. Empty fields fall back to the niche Unsplash pool.
                  </p>
                  {(project?.services || []).length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No services configured yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {(project?.services || []).map((service, idx) => (
                        <div key={service.slug || idx} className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
                          <div className="text-sm font-medium text-slate-700 truncate" title={service.name}>
                            {service.name}
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              value={(editForm.service_images || [])[idx] || ''}
                              onChange={e => {
                                const next = [...(editForm.service_images || [])];
                                next[idx] = e.target.value;
                                setEditForm(prev => ({ ...prev, service_images: next }));
                              }}
                              placeholder="https://cdn.example.com/services/drain-cleaning.jpg"
                              data-testid={`edit-service-image-${idx}`}
                              className="flex-1"
                            />
                            {((editForm.service_images || [])[idx] || '').trim() && (
                              <img
                                src={(editForm.service_images || [])[idx]}
                                alt={`${service.name} preview`}
                                className="w-12 h-12 rounded border object-cover bg-slate-100"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {(project?.template === 'admin-gestion-fr' || project?.template === 'admin-gestion-premium') && (
            <Card>
              <CardHeader>
                <CardTitle>Mentions légales (FR)</CardTitle>
                <CardDescription>Informations affichées dans un bandeau sous le footer. Laissez vide pour masquer le bandeau.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Forme juridique</Label>
                    <Input
                      value={editForm.legal_form}
                      onChange={e => setEditForm(prev => ({...prev, legal_form: e.target.value}))}
                      placeholder="SAS, EURL, Entreprise Individuelle…"
                      data-testid="edit-legal-form"
                    />
                  </div>
                  <div>
                    <Label>SIRET</Label>
                    <Input
                      value={editForm.siret}
                      onChange={e => setEditForm(prev => ({...prev, siret: e.target.value}))}
                      placeholder="123 456 789 00012"
                      data-testid="edit-siret"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>URL Politique de confidentialité (RGPD) <span className="text-slate-400 text-xs font-normal">(optionnel)</span></Label>
                    <Input
                      value={editForm.rgpd_url}
                      onChange={e => setEditForm(prev => ({...prev, rgpd_url: e.target.value}))}
                      placeholder="Laissez vide pour pointer vers la page privacy-policy générée"
                      data-testid="edit-rgpd-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Par défaut, le lien du bandeau pointe vers <code>privacy-policy.html</code>. Si votre politique RGPD est hébergée ailleurs, collez l'URL complète ici.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Cities & Directions Embeds</CardTitle>
              <CardDescription>Paste a Google Maps directions embed code per city — shows a map on city pages linking your main location to each sub-city</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(project?.cities || []).map((city, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`city-embed-${idx}`}>
                    <p className="text-sm font-medium text-slate-700">{city.name}, {city.state}</p>
                    <textarea
                      placeholder='Paste Google Maps embed code: <iframe src="https://www.google.com/maps/embed?..." ...></iframe>'
                      className="w-full min-h-[60px] px-3 py-2 border rounded-md text-xs font-mono"
                      defaultValue={city.directions_embed || ''}
                      onBlur={async (e) => {
                        const val = e.target.value.trim();
                        try {
                          const updatedCities = [...(project.cities || [])];
                          updatedCities[idx] = {...updatedCities[idx], directions_embed: val};
                          await axios.put(`${API}/projects/${projectId}`, { cities: updatedCities });
                          toast.success(`Directions saved for ${city.name}`);
                          await fetchProject();
                        } catch(err) { toast.error("Failed to save"); }
                      }}
                    />
                  </div>
                ))}
                {(!project?.cities || project.cities.length === 0) && (
                  <p className="text-sm text-slate-400 italic">No surrounding cities configured for this project.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Testimonials</CardTitle>
              <CardDescription>Edit, add, or remove customer reviews. Changes save automatically. Reviews only render on the site when at least one testimonial is present.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(project?.testimonials || []).map((t, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`testimonial-edit-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Testimonial #{idx + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const updated = (project.testimonials || []).filter((_, i) => i !== idx);
                            await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                            toast.success("Testimonial removed");
                            await fetchProject();
                          } catch (err) { toast.error("Failed to remove"); }
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        data-testid={`testimonial-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input
                          defaultValue={t.name || ''}
                          placeholder="Jane Doe"
                          onBlur={async (e) => {
                            const val = e.target.value;
                            if (val === (t.name || '')) return;
                            try {
                              const updated = [...(project.testimonials || [])];
                              updated[idx] = { ...updated[idx], name: val };
                              await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                              toast.success("Saved");
                              await fetchProject();
                            } catch (err) { toast.error("Failed to save"); }
                          }}
                          data-testid={`testimonial-name-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Location</Label>
                        <Input
                          defaultValue={t.location || ''}
                          placeholder="Chicago, IL"
                          onBlur={async (e) => {
                            const val = e.target.value;
                            if (val === (t.location || '')) return;
                            try {
                              const updated = [...(project.testimonials || [])];
                              updated[idx] = { ...updated[idx], location: val };
                              await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                              toast.success("Saved");
                              await fetchProject();
                            } catch (err) { toast.error("Failed to save"); }
                          }}
                          data-testid={`testimonial-location-${idx}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Rating</Label>
                      <div className="flex items-center gap-1 mt-1" data-testid={`testimonial-rating-${idx}`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={async () => {
                              try {
                                const updated = [...(project.testimonials || [])];
                                updated[idx] = { ...updated[idx], rating: star };
                                await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                                await fetchProject();
                              } catch (err) { toast.error("Failed to save"); }
                            }}
                            className="p-0.5 transition-colors"
                          >
                            <Star className={`w-5 h-5 ${star <= (t.rating || 0) ? "fill-yellow-400 text-yellow-400" : "fill-none text-slate-300"}`} />
                          </button>
                        ))}
                        <span className="text-sm text-slate-500 ml-2">{t.rating || 0}/5</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Review Text</Label>
                      <textarea
                        defaultValue={t.text || ''}
                        placeholder="Great service! They were professional…"
                        rows={3}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        onBlur={async (e) => {
                          const val = e.target.value;
                          if (val === (t.text || '')) return;
                          try {
                            const updated = [...(project.testimonials || [])];
                            updated[idx] = { ...updated[idx], text: val };
                            await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                            toast.success("Saved");
                            await fetchProject();
                          } catch (err) { toast.error("Failed to save"); }
                        }}
                        data-testid={`testimonial-text-${idx}`}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full border-dashed"
                  onClick={async () => {
                    try {
                      const updated = [...(project.testimonials || []), { name: "", location: "", rating: 5, text: "" }];
                      await axios.put(`${API}/projects/${projectId}`, { testimonials: updated });
                      await fetchProject();
                    } catch (err) { toast.error("Failed to add"); }
                  }}
                  data-testid="add-testimonial-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Testimonial
                </Button>
                {(!project?.testimonials || project.testimonials.length === 0) && (
                  <p className="text-xs text-slate-400 italic mt-2">No testimonials yet — reviews schema will not be rendered until at least one is added (compliance: no fake reviews).</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Advanced</CardTitle>
              <CardDescription>Custom scripts and embeds</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div>
                  <Label>Google Maps Embed URL</Label>
                  <Input value={editForm.google_maps_embed} onChange={e => setEditForm(prev => ({...prev, google_maps_embed: e.target.value}))} placeholder="https://www.google.com/maps/embed?..." data-testid="edit-maps" />
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      type="button"
                      data-testid="refresh-maps-embeds-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/projects/${projectId}/refresh-maps-embeds`);
                          const { homepage_filled, cities_filled, total_cities } = res.data;
                          if (!homepage_filled && cities_filled === 0) {
                            toast.info("Aucun slot vide — toutes les cartes sont déjà peuplées.");
                          } else {
                            const parts = [];
                            if (homepage_filled) parts.push("carte d'accueil");
                            if (cities_filled > 0) parts.push(`${cities_filled}/${total_cities} ville(s)`);
                            toast.success(`Maps rafraîchies : ${parts.join(" + ")}.`);
                            // Reload project so the editor reflects the new values.
                            const refreshed = await axios.get(`${API}/projects/${projectId}`);
                            setProject(refreshed.data);
                            setEditForm(prev => ({...prev, google_maps_embed: refreshed.data.google_maps_embed || ''}));
                          }
                        } catch (err) {
                          toast.error("Erreur lors du rafraîchissement des cartes : " + (err?.response?.data?.detail || err.message));
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir les cartes (homepage + villes)
                    </button>
                    <span className="text-xs text-slate-500">
                      Remplit uniquement les slots vides — vos overrides manuels sont préservés.
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Mobile sticky CTA label <span className="text-xs text-slate-400">(bottom bar on phones — leave empty for default "Call now: {`{phone}`}")</span></Label>
                  <Input value={editForm.mobile_sticky_cta_label} onChange={e => setEditForm(prev => ({...prev, mobile_sticky_cta_label: e.target.value}))} placeholder="Devis gratuit sous 24h — 06 50 33 58 19" data-testid="edit-mobile-sticky-cta-label" />
                </div>
                {project?.template === 'couvreur-premium' && (
                  <div>
                    <Label>Second numéro de téléphone (footer) <span className="text-xs text-slate-400">(Couvreur Premium uniquement — affiché dans le footer juste sous le téléphone principal)</span></Label>
                    <Input value={editForm.phone_secondary || ''} onChange={e => setEditForm(prev => ({...prev, phone_secondary: e.target.value}))} placeholder="06 12 34 56 78" data-testid="edit-phone-secondary" />
                  </div>
                )}
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <Label className="text-sm font-semibold text-slate-800">Site role — Rank-and-rent compliance</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Lead-network mode swaps the salesy hero, footer disclaimer
                    and JSON-LD schema for neutral, FTC/Google-compliant wording
                    (no fake provider claims). Use this when the site refers
                    visitors to independent local pros.
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="site_role"
                        value="direct_provider"
                        checked={(editForm.site_role || 'direct_provider') === 'direct_provider'}
                        onChange={() => setEditForm(prev => ({...prev, site_role: 'direct_provider'}))}
                        className="mr-2"
                        data-testid="site-role-direct"
                      />
                      <span className="text-sm">Direct provider <span className="text-xs text-slate-400">(default — the site IS the contractor)</span></span>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="site_role"
                        value="lead_network"
                        checked={editForm.site_role === 'lead_network'}
                        onChange={() => setEditForm(prev => ({...prev, site_role: 'lead_network'}))}
                        className="mr-2"
                        data-testid="site-role-network"
                      />
                      <span className="text-sm">Lead network <span className="text-xs text-slate-400">(rank-and-rent — connects users to local pros)</span></span>
                    </label>
                  </div>
                  {editForm.site_role === 'lead_network' && (
                    <div className="space-y-3 pl-2 border-l-2 border-orange-200 ml-2">
                      <div>
                        <Label className="text-xs">Network name <span className="text-slate-400">(optional — shown as H1 instead of business name)</span></Label>
                        <Input value={editForm.network_name || ''} onChange={e => setEditForm(prev => ({...prev, network_name: e.target.value}))} placeholder="Chicago Sewer Repair Resource & Service Network" data-testid="edit-network-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Footer disclaimer override <span className="text-slate-400">(optional — leave empty for the localized default)</span></Label>
                        <textarea value={editForm.network_disclaimer || ''} onChange={e => setEditForm(prev => ({...prev, network_disclaimer: e.target.value}))} className="w-full min-h-[60px] px-3 py-2 border rounded-md text-xs" placeholder="We may connect users with independent service providers. We are not a licensed contractor..." data-testid="edit-network-disclaimer" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <Label className="text-sm font-semibold text-slate-800">Service-area business (no physical office)</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Hide the street address everywhere (footer, contact page,
                    JSON-LD schema). The footer instead shows a "Serving" block
                    with your cities as clickable pills + an optional region
                    label (e.g. "Chicago Metro", "Chicagoland", "Métropole de
                    Lyon"). Recommended for rank-and-rent sites and any
                    business without a public storefront.
                  </p>
                  <label className="inline-flex items-center cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={!!editForm.service_area_business}
                      onChange={e => setEditForm(prev => ({...prev, service_area_business: e.target.checked}))}
                      className="mr-2"
                      data-testid="toggle-service-area-business"
                    />
                    <span className="text-sm">Enable service-area business mode</span>
                  </label>
                  {editForm.service_area_business && (
                    <div className="pl-2 border-l-2 border-orange-200 ml-2">
                      <Label className="text-xs">Region/metro label <span className="text-slate-400">(optional — appended to the Serving chips)</span></Label>
                      <Input
                        value={editForm.service_area_suffix || ''}
                        onChange={e => setEditForm(prev => ({...prev, service_area_suffix: e.target.value}))}
                        placeholder="Chicago Metro · Chicagoland · Métropole de Lyon · Seine-et-Marne (77)"
                        data-testid="edit-service-area-suffix"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Head Scripts <span className="text-xs text-slate-400">(analytics, tracking pixels)</span></Label>
                  <textarea value={editForm.head_scripts} onChange={e => setEditForm(prev => ({...prev, head_scripts: e.target.value}))} className="w-full min-h-[60px] px-3 py-2 border rounded-md text-xs font-mono" placeholder="<!-- Google Analytics, etc. -->" data-testid="edit-head-scripts" />
                </div>
                <div>
                  <Label>Body Scripts <span className="text-xs text-slate-400">(chat widgets, etc.)</span></Label>
                  <textarea value={editForm.body_scripts} onChange={e => setEditForm(prev => ({...prev, body_scripts: e.target.value}))} className="w-full min-h-[60px] px-3 py-2 border rounded-md text-xs font-mono" placeholder="<!-- Chatbot script, etc. -->" data-testid="edit-body-scripts" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button
              onClick={async () => {
                setSaving(true);
                try {
                  // Merge per-service image URLs into the services array,
                  // strip the helper `service_images` key (not part of the
                  // backend Project model), and submit.
                  const { service_images = [], ...rest } = editForm;
                  const baseServices = project?.services || [];
                  const updatedServices = baseServices.map((s, i) => ({
                    ...s,
                    image_url: (service_images[i] || '').trim(),
                  }));
                  const payload = { ...rest, services: updatedServices };
                  await axios.put(`${API}/projects/${projectId}`, payload);
                  toast.success("Project settings saved! Preview to see changes, then re-deploy.");
                  await fetchProject();
                  setEditForm(null);
                  setTimeout(() => {
                    if (project) setEditForm({ business_name: project.business_name || '', phone: project.phone || '', email: project.email || '', address: project.address || '', city: project.city || '', state: project.state || '', zip_code: project.zip_code || '', tagline: project.tagline || '', description: project.description || '', domain_name: project.domain_name || '', primary_color: project.primary_color || '#ea580c', secondary_color: project.secondary_color || '#0f172a', whatsapp: project.whatsapp || '', google_maps_embed: project.google_maps_embed || '', mobile_sticky_cta_label: project.mobile_sticky_cta_label || '', disclaimer: project.disclaimer || '', head_scripts: project.head_scripts || '', body_scripts: project.body_scripts || '', show_hero_stats: project.show_hero_stats || false, show_trust_badges: project.show_trust_badges || false, show_service_areas: project.show_service_areas != null ? project.show_service_areas : !(project.template || '').startsWith('admin-gestion'), logo_custom_url: project.logo_custom_url || '', logo_footer_custom_url: project.logo_footer_custom_url || '', favicon_custom_url: project.favicon_custom_url || '', wp_default_thumbnail_url: project.wp_default_thumbnail_url || '', why_image_url: project.why_image_url || '', about_image_url: project.about_image_url || '', founder_photo_url: project.founder_photo_url || '', services_layout: project.services_layout || 'grid', service_images: (project.services || []).map(s => s.image_url || ''), siret: project.siret || '', legal_form: project.legal_form || '', rgpd_url: project.rgpd_url || '', phone_secondary: project.phone_secondary || '', site_role: project.site_role || 'direct_provider', network_name: project.network_name || '', network_disclaimer: project.network_disclaimer || '', service_area_business: project.service_area_business || false, service_area_suffix: project.service_area_suffix || '' });
                  }, 500);
                } catch (e) { toast.error("Failed to save: " + (e.response?.data?.detail || e.message)); }
                finally { setSaving(false); }
              }}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-settings-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
            <span className="text-xs text-slate-500">After saving, preview your changes and re-deploy to update the live site.</span>
          </div>
        </div>
      )}


      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant={previewPage === "home" && !previewSlug ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("home"); setPreviewSlug(""); }}>Home</Button>
            <Button variant={previewPage === "about" ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("about"); setPreviewSlug(""); }}>About</Button>
            <Button variant={previewPage === "contact" ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("contact"); setPreviewSlug(""); }}>Contact</Button>
            <Button variant={previewPage === "privacy" ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("privacy"); setPreviewSlug(""); }}>Privacy</Button>
            {['couvreur-premium','bathroom-remodel','sewer-line','siding','admin-gestion-fr','admin-gestion-premium'].includes(project.template) && (
              <Button variant={previewPage === "glossary" ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("glossary"); setPreviewSlug(""); }} data-testid="preview-glossary-button">Glossary</Button>
            )}
            {project.blog_enabled && project.blog_articles?.length > 0 && (
              <Button variant={previewPage === "blog" && !previewSlug ? "default" : "outline"} size="sm" onClick={() => { setPreviewPage("blog"); setPreviewSlug(""); }}>Blog</Button>
            )}
            <Select value={previewPage === "service" ? previewSlug : ""} onValueChange={(value) => { setPreviewPage("service"); setPreviewSlug(value); }}>
              <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="Service Pages" /></SelectTrigger>
              <SelectContent>{project.services?.map((s) => (<SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>))}</SelectContent>
            </Select>
            <Select value={previewPage === "city" ? previewSlug : ""} onValueChange={(value) => { setPreviewPage("city"); setPreviewSlug(value); }}>
              <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="Location Pages" /></SelectTrigger>
              <SelectContent>{project.cities?.map((c) => (<SelectItem key={c.slug} value={c.slug}>{c.name}, {c.state}</SelectItem>))}</SelectContent>
            </Select>
            {project.blog_enabled && project.blog_articles?.filter(a => a.content)?.length > 0 && (
              <Select value={previewPage === "blog-article" ? previewSlug : ""} onValueChange={(value) => { setPreviewPage("blog-article"); setPreviewSlug(value); }}>
                <SelectTrigger className="w-[200px] h-9"><SelectValue placeholder="Blog Articles" /></SelectTrigger>
                <SelectContent>{project.blog_articles?.filter(a => a.content).map((a) => (<SelectItem key={a.slug} value={a.slug}>{a.title}</SelectItem>))}</SelectContent>
              </Select>
            )}
          </div>
          <div className="border rounded-lg overflow-hidden bg-white" style={{ height: "70vh" }}>
            <iframe src={previewUrl} className="w-full h-full" title="Website Preview" />
          </div>
        </>
      )}

      {/* AI Content Tab */}
      {activeTab === 'ai-content' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Homepage Content</CardTitle><CardDescription>Generate deep, SEO-optimized content for your homepage pillar page — hero description, service summaries, and why choose us</CardDescription></CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">Homepage Pillar Page</p>
                  <p className="text-sm text-slate-500">{project.homepage_content ? <span className="text-green-600">AI content generated — hero description, {Object.keys(project.homepage_content.services_content || {}).length} service summaries, why choose us</span> : <span className="text-slate-400">No AI content yet — using template defaults</span>}</p>
                </div>
                <Button
                  onClick={generateHomepageContent}
                  disabled={aiGenerating}
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="generate-homepage-btn"
                >
                  {aiGenerating ? "Generating..." : project.homepage_content ? "Regenerate" : "Generate Content"}
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-orange-500" />AI Content Generator</CardTitle>
              <CardDescription>Generate unique, SEO-optimized content for your service and city pages. Optionally provide a Wikipedia link for entity-rich content.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Wikipedia URL (optional)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={wikiUrl} onChange={(e) => setWikiUrl(e.target.value)} placeholder="https://en.wikipedia.org/wiki/Drain_cleaning" className="flex-1" />
                    <Button variant="outline" onClick={fetchWikiPreview} disabled={!wikiUrl}>Preview</Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Add a Wikipedia link related to the service or city for entity-rich content</p>
                </div>
                {wikiPreview && (
                  <div className="bg-slate-50 p-4 rounded-lg border max-h-40 overflow-y-auto">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{wikiPreview.slice(0, 500)}...</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>Service Pages</CardTitle>
                <CardDescription>Generate unique content for each service page</CardDescription>
              </div>
              <Button
                onClick={generateAllServiceContent}
                disabled={aiGenerating || !(project?.services?.length)}
                className="bg-orange-500 hover:bg-orange-600 shrink-0"
                title="Generates AI content only for service pages that don't have any yet (already-generated pages are skipped)."
                data-testid="generate-all-services-btn"
              >
                {bulkSinglePageProgress?.kind === "service"
                  ? `Generating ${bulkSinglePageProgress.current}/${bulkSinglePageProgress.total}…`
                  : (() => {
                      const remaining = (project?.services || []).filter((s) => !s.ai_content).length;
                      const total = project?.services?.length || 0;
                      return remaining < total
                        ? `Generate Missing (${remaining}/${total})`
                        : `Generate All (${total})`;
                    })()}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {project.services?.map((service) => (
                  <div key={service.slug} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-slate-500">{service.ai_content ? <span className="text-green-600">AI content generated</span> : <span className="text-slate-400">No AI content yet</span>}</p>
                    </div>
                    <Button onClick={() => generateServiceContent(service)} disabled={aiGenerating} size="sm" className="bg-orange-500 hover:bg-orange-600">{aiGenerating ? "Generating..." : "Generate Content"}</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>City Pages</CardTitle>
                <CardDescription>Generate unique local content for each city</CardDescription>
              </div>
              <Button
                onClick={generateAllCityContent}
                disabled={aiGenerating || !(project?.cities?.length)}
                className="bg-orange-500 hover:bg-orange-600 shrink-0"
                title="Generates AI content only for city pages that don't have any yet (already-generated pages are skipped)."
                data-testid="generate-all-cities-btn"
              >
                {bulkSinglePageProgress?.kind === "city"
                  ? `Generating ${bulkSinglePageProgress.current}/${bulkSinglePageProgress.total}…`
                  : (() => {
                      const remaining = (project?.cities || []).filter((c) => !c.ai_content).length;
                      const total = project?.cities?.length || 0;
                      return remaining < total
                        ? `Generate Missing (${remaining}/${total})`
                        : `Generate All (${total})`;
                    })()}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {project.cities?.map((city) => (
                  <div key={city.slug} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{city.name}, {city.state}</p>
                      <p className="text-sm text-slate-500">{city.ai_content ? <span className="text-green-600">AI content generated</span> : <span className="text-slate-400">No AI content yet</span>}</p>
                    </div>
                    <Button onClick={() => generateCityContent(city)} disabled={aiGenerating} size="sm" className="bg-orange-500 hover:bg-orange-600">{aiGenerating ? "Generating..." : "Generate Content"}</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {project.cities?.length > 0 && project.services?.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle>City + Service Pages</CardTitle>
                  <CardDescription>Generate hyper-local content for each city+service combination</CardDescription>
                </div>
                <Button
                  onClick={generateAllCityServiceContent}
                  disabled={aiGenerating}
                  className="bg-orange-500 hover:bg-orange-600 shrink-0"
                  title="Generates AI content only for city + service combos that don't have any yet (already-generated combos are skipped)."
                  data-testid="generate-all-combos-btn"
                >
                  {bulkSinglePageProgress?.kind === "combo"
                    ? `Generating ${bulkSinglePageProgress.current}/${bulkSinglePageProgress.total}…`
                    : (() => {
                        const total = (project?.cities?.length || 0) * (project?.services?.length || 0);
                        const cc = project?.combo_content || {};
                        let remaining = 0;
                        (project?.cities || []).forEach((city) =>
                          (project?.services || []).forEach((service) => {
                            if (!cc[`${city.slug}-${service.slug}`]) remaining += 1;
                          })
                        );
                        return remaining < total
                          ? `Generate Missing (${remaining}/${total})`
                          : `Generate All (${total})`;
                      })()}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {project.cities?.map((city) => (
                    project.services?.map((service) => {
                      const comboKey = `${city.slug}-${service.slug}`;
                      const hasContent = project.combo_content?.[comboKey];
                      return (
                        <div key={comboKey} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{service.name} in {city.name}</p>
                            <p className="text-xs text-slate-500">{hasContent ? <span className="text-green-600">AI content generated</span> : <span className="text-slate-400">No AI content yet</span>}</p>
                          </div>
                          <Button onClick={() => generateCityServiceContent(city, service)} disabled={aiGenerating} size="sm" variant="outline">Generate</Button>
                        </div>
                      );
                    })
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Blog Articles */}
          <Card>
            <CardHeader>
              <CardTitle>Blog Articles</CardTitle>
              <CardDescription>Generate SEO-optimized blog articles to boost organic traffic. Enter a topic and AI will write a full article.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={blogTopic}
                    onChange={(e) => setBlogTopic(e.target.value)}
                    placeholder="e.g. How to Choose the Right Roofing Material"
                    className="flex-1"
                    data-testid="blog-topic-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') generateBlogArticle(); }}
                  />
                  <Button
                    onClick={generateBlogArticle}
                    disabled={blogGenerating || !blogTopic.trim()}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="blog-generate-btn"
                  >
                    {blogGenerating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>) : "Generate Article"}
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 whitespace-nowrap">Schedule for:</Label>
                    <Input
                      type="date"
                      value={blogPublishDate}
                      onChange={(e) => setBlogPublishDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-44 h-8 text-xs"
                      data-testid="blog-publish-date"
                    />
                    {blogPublishDate && (
                      <button onClick={() => setBlogPublishDate("")} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">{blogPublishDate ? `Article will be scheduled for ${blogPublishDate}` : "Leave empty to publish immediately"}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={suggestBlogTopics}
                  disabled={suggestingTopics}
                  data-testid="blog-suggest-topics-btn"
                  className="w-fit"
                >
                  {suggestingTopics ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Suggesting...</>) : (<><Sparkles className="w-3.5 h-3.5 mr-1.5" />Suggest Topics with AI</>)}
                </Button>
                {suggestedTopics.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1.5" data-testid="suggested-topics-list">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Suggested Topics — click to use</p>
                    {suggestedTopics.map((topic, i) => (
                      <button
                        key={i}
                        onClick={() => { setBlogTopic(topic); setSuggestedTopics(prev => prev.filter((_, idx) => idx !== i)); }}
                        className="block w-full text-left text-sm px-3 py-1.5 rounded hover:bg-orange-50 hover:text-orange-700 transition-colors text-slate-700"
                        data-testid={`suggested-topic-${i}`}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                )}
                {project.blog_articles?.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <p className="text-sm font-medium text-slate-700">{project.blog_articles.length} article{project.blog_articles.length > 1 ? 's' : ''} — {project.blog_articles.filter(a => a.status === 'published' || a.published).length} published, {project.blog_articles.filter(a => a.status === 'scheduled').length} scheduled</p>
                    {project.blog_articles.map((article) => {
                      const isScheduled = article.status === 'scheduled';
                      const isReady = isScheduled && article.publish_date && new Date(article.publish_date) <= new Date();
                      return (
                        <div key={article.slug} className={`flex items-center justify-between p-3 rounded-lg ${isScheduled ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`blog-article-${article.slug}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm truncate">{article.title}</p>
                              {isScheduled ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600 shrink-0">{isReady ? 'Ready' : `Scheduled ${article.publish_date}`}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-400 text-green-600 shrink-0">Published</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{article.excerpt || article.meta_description}</p>
                            <div className="flex gap-1 mt-1">{article.tags?.slice(0, 3).map((t, i) => (<Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">{t}</Badge>))}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {isScheduled && (
                              <Button variant="ghost" size="sm" onClick={() => publishBlogArticle(article.slug)} className="text-green-600 hover:text-green-700 text-xs" data-testid={`blog-publish-${article.slug}`}>Publish</Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => deleteBlogArticle(article.slug)} className="text-red-500 hover:text-red-600" data-testid={`blog-delete-${article.slug}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!project.blog_enabled && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Blog is currently disabled for this project. Articles will be generated but not included in the website until blog is enabled.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SEO Audit Tab */}
      {activeTab === 'seo-audit' && (
        <div data-testid="seo-audit-content">
          {seoLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500">Running SEO audit on all pages...</p>
              </div>
            </div>
          ) : seoReport ? (
            <div className="space-y-6">
              <Card data-testid="seo-overall-score">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-6">
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ${seoReport.overall_score >= 90 ? 'bg-green-500' : seoReport.overall_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}>{seoReport.overall_score}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900">Overall SEO Score: {seoReport.overall_score}/100</h3>
                      <p className="text-sm text-slate-500 mt-1">Audited {seoReport.pages_audited} pages with {seoReport.total_checks} checks</p>
                      <div className="flex items-center gap-4 mt-3">
                        <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> {seoReport.passed} passed</span>
                        <span className="flex items-center gap-1 text-sm text-yellow-600"><AlertTriangle className="w-4 h-4" /> {seoReport.warnings} warnings</span>
                        <span className="flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> {seoReport.failed} failed</span>
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => { setSeoLoading(true); setSeoReport(null); axios.get(`${API}/projects/${projectId}/seo-audit`).then(res => setSeoReport(res.data)).catch(() => toast.error("Failed to run SEO audit")).finally(() => setSeoLoading(false)); }} data-testid="seo-rerun-btn">Re-run Audit</Button>
                    {(seoReport.warnings > 0 || seoReport.failed > 0) && (
                      <Button onClick={() => { setFixingSeo(true); axios.post(`${API}/projects/${projectId}/fix-seo`).then(res => { const data = res.data; if (data.success && data.report) { setSeoReport(data.report); toast.success(`Fixed ${data.fixes_applied} pages: ${data.old_score} → ${data.new_score}/100`); } else if (data.success) { toast.success(data.message); } else { toast.error(data.message || "Fix failed"); } }).catch(() => toast.error("Failed to fix SEO issues")).finally(() => setFixingSeo(false)); }} disabled={fixingSeo} className="bg-orange-500 hover:bg-orange-600" data-testid="seo-fix-btn">
                        {fixingSeo ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Fixing with AI...</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Fix SEO Issues</>)}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-3">
                {seoReport.pages.map((page, idx) => (
                  <Card key={idx} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedPage(expandedPage === idx ? null : idx)} data-testid={`seo-page-${idx}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${page.score >= 90 ? 'bg-green-500' : page.score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}>{page.score}</div>
                          <div><p className="font-medium text-slate-900">{page.page}</p><p className="text-xs text-slate-500 truncate max-w-md">{page.summary.title}</p></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-green-600">{page.checks.filter(c => c.status === 'pass').length} pass</span>
                            <span className="text-yellow-600">{page.checks.filter(c => c.status === 'warn').length} warn</span>
                            <span className="text-red-600">{page.checks.filter(c => c.status === 'fail').length} fail</span>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedPage === idx ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      {expandedPage === idx && (
                        <div className="mt-4 pt-4 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                          {page.checks.map((check, ci) => (
                            <div key={ci} className={`flex items-start gap-3 p-2 rounded-lg text-sm ${check.status === 'pass' ? 'bg-green-50' : check.status === 'warn' ? 'bg-yellow-50' : 'bg-red-50'}`} data-testid={`seo-check-${idx}-${ci}`}>
                              {check.status === 'pass' ? <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" /> : check.status === 'warn' ? <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-slate-800">{check.name}</span>
                                <span className="text-slate-500 ml-2">({check.score}/{check.max})</span>
                                <p className="text-slate-600 text-xs mt-0.5">{check.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Click the SEO Audit tab to run an audit</p>
            </div>
          )}
        </div>
      )}

      {/* WordPress Tab */}
      {activeTab === 'wordpress' && (
        <div data-testid="wordpress-content" className="space-y-6 max-w-2xl">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Push to WordPress</h3>
            <p className="text-sm text-slate-500 mt-1">Connect your WordPress site and publish all generated pages automatically.</p>
          </div>
          <Card>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Globe2 className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm text-slate-700">WordPress Connection</span>
                {wpConnected && <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>}
              </div>
              <div><Label>WordPress Site URL *</Label><Input value={wpUrl} onChange={(e) => { setWpUrl(e.target.value); setWpConnected(false); }} placeholder="https://your-site.com" data-testid="wp-url-input" /></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>WordPress Username *</Label><Input value={wpUser} onChange={(e) => { setWpUser(e.target.value); setWpConnected(false); }} placeholder="admin" data-testid="wp-user-input" /></div>
                <div><Label>Application Password *</Label><Input type="password" value={wpPass} onChange={(e) => { setWpPass(e.target.value); setWpConnected(false); }} placeholder="xxxx xxxx xxxx xxxx" data-testid="wp-pass-input" /></div>
              </div>
              <p className="text-xs text-slate-400">Generate an Application Password in WordPress: Users &rarr; Profile &rarr; Application Passwords</p>
              <Button variant="outline" onClick={async () => {
                if (!wpUrl || !wpUser || !wpPass) { toast.error("Fill in all WordPress fields"); return; }
                setWpTesting(true);
                try {
                  const res = await axios.post(`${API}/projects/${projectId}/wp/test-connection`, { site_url: wpUrl, username: wpUser, app_password: wpPass });
                  if (res.data.success) { setWpConnected(true); toast.success(`Connected as ${res.data.user}`); } else { toast.error(res.data.error); }
                } catch (e) { toast.error("Connection test failed"); }
                finally { setWpTesting(false); }
              }} disabled={wpTesting || !wpUrl || !wpUser || !wpPass} data-testid="wp-test-btn">
                {wpTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</> : <><Link2 className="w-4 h-4 mr-2" /> Test Connection</>}
              </Button>
            </CardContent>
          </Card>
          {wpConnected && (
            <Card data-testid="wp-page-selection">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900">Page selection & locks</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Lock pages you've manually edited on WordPress so they're skipped on the next publish.{" "}
                      <strong>{wpLockedSlugs.size}</strong> locked / <strong>{wpPagesList.length}</strong> total.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setWpPagesLoading(true);
                      try {
                        const res = await axios.get(`${API}/projects/${projectId}/wp/pages`);
                        setWpPagesList(res.data.pages || []);
                        setWpLockedSlugs(new Set(res.data.locked_slugs || []));
                      } catch (e) {
                        toast.error("Failed to load WP pages list");
                      } finally {
                        setWpPagesLoading(false);
                      }
                    }}
                    disabled={wpPagesLoading}
                    data-testid="wp-load-pages-btn"
                  >
                    {wpPagesLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    {wpPagesList.length === 0 ? "Load pages" : "Reload"}
                  </Button>
                </div>

                {wpPagesList.length > 0 && (
                  <>
                    {/* Group action buttons */}
                    <div className="flex flex-wrap gap-2 pb-2 border-b border-slate-100">
                      {[
                        { label: "All", types: null },
                        { label: "Services", types: ["service"] },
                        { label: "Locations", types: ["city", "city-service", "locations-hub"] },
                        { label: "Blog", types: ["blog-article", "blog-hub"] },
                      ].map((grp) => (
                        <div key={grp.label} className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">{grp.label}:</span>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                            onClick={() => {
                              const next = new Set(wpLockedSlugs);
                              wpPagesList.forEach((p) => {
                                if (!grp.types || grp.types.includes(p.page_type)) next.add(p.slug);
                              });
                              setWpLockedSlugs(next);
                            }}
                            data-testid={`wp-lock-group-${grp.label.toLowerCase()}`}
                          >Lock all</button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                            onClick={() => {
                              const next = new Set(wpLockedSlugs);
                              wpPagesList.forEach((p) => {
                                if (!grp.types || grp.types.includes(p.page_type)) next.delete(p.slug);
                              });
                              setWpLockedSlugs(next);
                            }}
                            data-testid={`wp-unlock-group-${grp.label.toLowerCase()}`}
                          >Unlock all</button>
                        </div>
                      ))}
                    </div>

                    {/* Per-page lock list */}
                    <div className="max-h-72 overflow-y-auto space-y-1 border border-slate-200 rounded p-2">
                      {wpPagesList.map((p) => {
                        const isLocked = wpLockedSlugs.has(p.slug);
                        return (
                          <label
                            key={p.slug}
                            className={`flex items-center justify-between text-sm py-1.5 px-2 rounded cursor-pointer ${
                              isLocked ? "bg-amber-50" : "hover:bg-slate-50"
                            }`}
                            data-testid={`wp-page-row-${p.slug}`}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={isLocked}
                                onChange={(e) => {
                                  const next = new Set(wpLockedSlugs);
                                  if (e.target.checked) next.add(p.slug);
                                  else next.delete(p.slug);
                                  setWpLockedSlugs(next);
                                }}
                                data-testid={`wp-lock-${p.slug}`}
                              />
                              <span className={isLocked ? "font-medium text-amber-700" : "text-slate-700"}>
                                {isLocked ? "🔒" : "🔓"} {p.title}
                              </span>
                              <code className="text-xs text-slate-400 truncate">/{p.slug}</code>
                            </span>
                            <Badge variant="outline" className="text-xs ml-2 capitalize">{p.page_type.replace(/-/g, " ")}</Badge>
                          </label>
                        );
                      })}
                    </div>

                    <Button
                      onClick={async () => {
                        setWpLocksSaving(true);
                        try {
                          await axios.put(`${API}/projects/${projectId}/wp/lock`, {
                            slugs: Array.from(wpLockedSlugs),
                          });
                          toast.success(`Saved (${wpLockedSlugs.size} locked)`);
                        } catch (e) {
                          toast.error("Failed to save locks");
                        } finally {
                          setWpLocksSaving(false);
                        }
                      }}
                      disabled={wpLocksSaving}
                      variant="outline"
                      className="w-full"
                      data-testid="wp-save-locks-btn"
                    >
                      {wpLocksSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save lock list
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
          {wpConnected && (
            <Card data-testid="wp-publish-section">
              <CardContent className="pt-5 space-y-4">
                <div><h4 className="font-medium text-slate-900">Publish Pages</h4><p className="text-sm text-slate-500 mt-1">This will create or update all pages on your WordPress site with the generated HTML, CSS, and JS. <strong>Locked pages are skipped.</strong></p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <p>Pages to publish:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-0.5">
                    <li>Home, About, Contact, Privacy Policy</li>
                    <li>{project?.services?.length || 0} service pages</li>
                    <li>{project?.cities?.length || 0} city pages</li>
                    <li>{(project?.services?.length || 0) * (project?.cities?.length || 0)} city+service pages</li>
                    <li>Full CSS styling + JavaScript (inlined)</li>
                  </ul>
                  {wpLockedSlugs.size > 0 && (
                    <p className="mt-2 text-amber-700">
                      <strong>{wpLockedSlugs.size}</strong> page{wpLockedSlugs.size > 1 ? "s" : ""} locked — will be skipped.
                    </p>
                  )}
                </div>
                <Button onClick={async () => {
                  setWpPublishing(true); setWpResult(null);
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/wp/publish`, {});
                    setWpResult(res.data);
                    const skippedCount = (res.data.skipped || []).length;
                    const skipMsg = skippedCount ? ` (${skippedCount} locked, skipped)` : "";
                    if (res.data.errors === 0) { toast.success(`Published ${res.data.published} pages${skipMsg}`); }
                    else { toast.warning(`Published ${res.data.published}/${res.data.total} pages (${res.data.errors} errors)${skipMsg}`); }
                  } catch (e) { toast.error("Publishing failed — check WordPress credentials and permissions"); }
                  finally { setWpPublishing(false); }
                }} disabled={wpPublishing} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="wp-publish-btn">
                  {wpPublishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing to WordPress...</> : <><Globe2 className="w-4 h-4 mr-2" /> Publish to WordPress</>}
                </Button>
                {wpLockedSlugs.size > 0 && (
                  <Button
                    onClick={async () => {
                      if (!window.confirm(`Force-republish ALL pages, including the ${wpLockedSlugs.size} locked one(s)? This will overwrite any manual edits you made on WordPress.`)) return;
                      setWpPublishing(true); setWpResult(null);
                      try {
                        const res = await axios.post(`${API}/projects/${projectId}/wp/publish`, { force_overwrite_locked: true });
                        setWpResult(res.data);
                        if (res.data.errors === 0) toast.success(`Force-published ${res.data.published} pages (locks ignored)`);
                        else toast.warning(`Published ${res.data.published}/${res.data.total} (${res.data.errors} errors)`);
                      } catch (e) { toast.error("Force-publish failed"); }
                      finally { setWpPublishing(false); }
                    }}
                    disabled={wpPublishing}
                    variant="outline"
                    className="w-full text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="wp-force-publish-btn"
                  >
                    Force republish all (overrides locks)
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
          {wpResult && (
            <Card data-testid="wp-result">
              <CardContent className="pt-5">
                <div className="flex items-center gap-3 mb-4">
                  {wpResult.errors === 0 ? <CheckCircle className="w-6 h-6 text-green-500" /> : <AlertTriangle className="w-6 h-6 text-yellow-500" />}
                  <div>
                    <p className="font-medium text-slate-900">{wpResult.errors === 0 ? `All ${wpResult.published} pages published successfully!` : `${wpResult.published}/${wpResult.total} pages published (${wpResult.errors} errors)`}</p>
                    <p className="text-sm text-slate-500">Pages are live on {wpUrl}</p>
                  </div>
                </div>
                {wpResult.pages && (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {wpResult.pages.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500" /><span className="text-slate-700">/{p.slug}</span><Badge variant="outline" className="text-xs">{p.status}</Badge></div>
                        {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline text-xs">View</a>}
                      </div>
                    ))}
                  </div>
                )}
                {wpResult.error_details && wpResult.error_details.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-sm font-medium text-red-600">Errors:</p>
                    {wpResult.error_details.map((e, i) => (<div key={i} className="flex items-center gap-2 text-sm text-red-500"><XCircle className="w-3.5 h-3.5" /><span>/{e.slug}: {e.error}</span></div>))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Bunny CDN Tab */}
      {activeTab === 'bunny-cdn' && (
        <div data-testid="bunny-cdn-content" className="space-y-6 max-w-2xl">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Deploy to Bunny CDN</h3>
            <p className="text-sm text-slate-500 mt-1">Upload all generated files to a Bunny.net Storage Zone for global CDN delivery.</p>
          </div>
          <Card>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Upload className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm text-slate-700">Storage Zone Connection</span>
                {bunnyConnected && <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>}
              </div>
              <div><Label>Storage Zone Name *</Label><Input value={bunnyZone} onChange={(e) => { setBunnyZone(e.target.value); setBunnyConnected(false); }} placeholder="my-storage-zone" data-testid="bunny-zone-input" /></div>
              <div><Label>Access Key (Password) *</Label><Input type="password" value={bunnyKey} onChange={(e) => { setBunnyKey(e.target.value); setBunnyConnected(false); }} placeholder="Your storage zone password" data-testid="bunny-key-input" /></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Remote Path (optional)</Label><Input value={bunnyPath} onChange={(e) => { setBunnyPath(e.target.value); setBunnyConnected(false); }} placeholder="sites/my-business" data-testid="bunny-path-input" /></div>
                <div>
                  <Label>Region</Label>
                  <Select value={bunnyRegion} onValueChange={(v) => { setBunnyRegion(v); setBunnyConnected(false); }}>
                    <SelectTrigger data-testid="bunny-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (Falkenstein)</SelectItem>
                      <SelectItem value="ny">New York</SelectItem>
                      <SelectItem value="la">Los Angeles</SelectItem>
                      <SelectItem value="sg">Singapore</SelectItem>
                      <SelectItem value="de">Germany</SelectItem>
                      <SelectItem value="uk">United Kingdom</SelectItem>
                      <SelectItem value="se">Stockholm</SelectItem>
                      <SelectItem value="br">Sao Paulo</SelectItem>
                      <SelectItem value="syd">Sydney</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Pull Zone URL (optional — for CDN links)</Label><Input value={bunnyPullUrl} onChange={(e) => setBunnyPullUrl(e.target.value)} placeholder="https://my-zone.b-cdn.net" data-testid="bunny-pull-url-input" /></div>
              <p className="text-xs text-slate-400">Find your Access Key in Bunny.net: Storage &rarr; Your Zone &rarr; FTP & API Access. Optionally connect a Pull Zone for CDN delivery.</p>
              <Button variant="outline" onClick={async () => {
                if (!bunnyZone || !bunnyKey) { toast.error("Storage Zone Name and Access Key are required"); return; }
                setBunnyTesting(true);
                try {
                  const res = await axios.post(`${API}/projects/${projectId}/bunny/test-connection`, { storage_zone: bunnyZone, access_key: bunnyKey, remote_path: bunnyPath, region: bunnyRegion, pull_zone_url: bunnyPullUrl });
                  if (res.data.success) { setBunnyConnected(true); toast.success(res.data.message); } else { toast.error(res.data.error); }
                } catch (e) { toast.error("Connection test failed"); }
                finally { setBunnyTesting(false); }
              }} disabled={bunnyTesting || !bunnyZone || !bunnyKey} data-testid="bunny-test-btn">
                {bunnyTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</> : <><Link2 className="w-4 h-4 mr-2" /> Test Connection</>}
              </Button>
            </CardContent>
          </Card>
          {bunnyConnected && (
            <Card data-testid="bunny-deploy-section">
              <CardContent className="pt-5 space-y-4">
                <div><h4 className="font-medium text-slate-900">Deploy Files</h4><p className="text-sm text-slate-500 mt-1">This will upload all generated HTML, CSS, JS, and logo files to your Bunny Storage Zone.</p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <p>Files to upload:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-0.5">
                    <li>Home, About, Contact, Privacy Policy (HTML)</li>
                    <li>{project?.services?.length || 0} service pages</li>
                    <li>{project?.cities?.length || 0} city pages + {(project?.services?.length || 0) * (project?.cities?.length || 0)} combo pages</li>
                    <li>CSS stylesheet + JavaScript</li>
                    {project?.has_logo && <li>Logo (WEBP)</li>}
                  </ul>
                  {bunnyPath && <p className="mt-2 text-xs text-slate-400">Remote path: /{bunnyPath}/</p>}
                </div>
                <Button onClick={async () => {
                  setBunnyDeploying(true); setBunnyResult(null);
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/bunny/deploy`);
                    setBunnyResult(res.data);
                    if (res.data.errors === 0) { toast.success(`Uploaded ${res.data.uploaded} files to Bunny CDN!`); }
                    else { toast.warning(`Uploaded ${res.data.uploaded}/${res.data.total} files (${res.data.errors} errors)`); }
                  } catch (e) { toast.error("Deployment failed — check Bunny CDN credentials and permissions"); }
                  finally { setBunnyDeploying(false); }
                }} disabled={bunnyDeploying} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="bunny-deploy-btn">
                  {bunnyDeploying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying to Bunny CDN...</> : <><Upload className="w-4 h-4 mr-2" /> Deploy to Bunny CDN</>}
                </Button>
              </CardContent>
            </Card>
          )}
          {bunnyResult && (
            <Card data-testid="bunny-result">
              <CardContent className="pt-5">
                <div className="flex items-center gap-3 mb-4">
                  {bunnyResult.errors === 0 ? <CheckCircle className="w-6 h-6 text-green-500" /> : <AlertTriangle className="w-6 h-6 text-yellow-500" />}
                  <div>
                    <p className="font-medium text-slate-900">{bunnyResult.errors === 0 ? `All ${bunnyResult.uploaded} files uploaded successfully!` : `${bunnyResult.uploaded}/${bunnyResult.total} files uploaded (${bunnyResult.errors} errors)`}</p>
                    <p className="text-sm text-slate-500">{bunnyPullUrl ? `Files served at ${bunnyPullUrl}${bunnyPath ? `/${bunnyPath}` : ''}` : `Files stored in zone '${bunnyZone}'`}</p>
                  </div>
                </div>
                {bunnyResult.files && (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {bunnyResult.files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500" /><span className="text-slate-700">{f.file}</span></div>
                        {f.cdn_url && <a href={f.cdn_url} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline text-xs">View</a>}
                      </div>
                    ))}
                  </div>
                )}
                {bunnyResult.error_details && bunnyResult.error_details.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-sm font-medium text-red-600">Errors:</p>
                    {bunnyResult.error_details.map((e, i) => (<div key={i} className="flex items-center gap-2 text-sm text-red-500"><XCircle className="w-3.5 h-3.5" /><span>{e.file}: {e.error}</span></div>))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Cloudflare Pages Tab */}
      {activeTab === 'cloudflare' && (
        <div className="space-y-6" data-testid="cloudflare-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Cloudflare Pages</CardTitle>
              <CardDescription>Deploy your website to Cloudflare Pages with global edge hosting, free SSL, and custom domain support.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>API Token</Label><Input type="password" value={cfApiToken} onChange={(e) => { setCfApiToken(e.target.value); setCfConnected(false); }} placeholder="cfut_xxxxxxxxxxxx" data-testid="cf-api-token-input" /></div>
                <div><Label>Account ID</Label><Input value={cfAccountId} onChange={(e) => { setCfAccountId(e.target.value); setCfConnected(false); }} placeholder="32-character hex string" data-testid="cf-account-id-input" /></div>
              </div>
              <p className="text-xs text-slate-400">Create an API token at <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Cloudflare Dashboard</a> with <strong>Cloudflare Pages: Edit</strong> permission. Account ID is in your dashboard sidebar.</p>
              <Button variant="outline" onClick={async () => {
                if (!cfApiToken || !cfAccountId) { toast.error("API Token and Account ID are required"); return; }
                setCfTesting(true);
                try {
                  const res = await axios.post(`${API}/projects/${projectId}/cloudflare/test-connection`, { api_token: cfApiToken, account_id: cfAccountId });
                  if (res.data.success) { setCfConnected(true); toast.success(res.data.message); } else { toast.error(res.data.error); }
                } catch (e) { toast.error("Connection test failed"); }
                finally { setCfTesting(false); }
              }} disabled={cfTesting || !cfApiToken || !cfAccountId} data-testid="cf-test-btn">
                {cfTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</> : <><Link2 className="w-4 h-4 mr-2" /> Test Connection</>}
              </Button>
            </CardContent>
          </Card>

          {cfConnected && (
            <Card data-testid="cf-deploy-section">
              <CardContent className="pt-5 space-y-4">
                <div><h4 className="font-medium text-slate-900">Deploy to Cloudflare Pages</h4><p className="text-sm text-slate-500 mt-1">Creates a Cloudflare Pages project and deploys all generated pages. Your site will be live at <strong>{(project?.business_name || 'your-site').toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')}.pages.dev</strong></p></div>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 space-y-1">
                  <p>Files to deploy:</p>
                  <ul className="list-disc list-inside text-slate-500 space-y-0.5">
                    <li>Home, About, Contact, Privacy Policy (HTML)</li>
                    <li>{project?.services?.length || 0} service pages</li>
                    <li>{project?.cities?.length || 0} city pages + {(project?.services?.length || 0) * (project?.cities?.length || 0)} combo pages</li>
                    {project?.blog_enabled && project?.blog_articles?.length > 0 && <li>{project.blog_articles.length} blog article(s)</li>}
                    <li>CSS, JavaScript, Sitemap, Robots.txt</li>
                    {project?.has_logo && <li>Logo (WEBP)</li>}
                  </ul>
                </div>
                <Button onClick={async () => {
                  setCfDeploying(true); setCfResult(null);
                  try {
                    const res = await axios.post(`${API}/projects/${projectId}/cloudflare/deploy`, {}, { timeout: 180000 });
                    setCfResult(res.data);
                    if (res.data.success) { toast.success(`Deployed to Cloudflare Pages!`); await fetchProject(); } else { toast.error(res.data.error || "Deployment failed"); }
                  } catch (e) { toast.error("Deployment failed — check credentials and try again"); }
                  finally { setCfDeploying(false); }
                }} disabled={cfDeploying} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="cf-deploy-btn">
                  {cfDeploying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying to Cloudflare Pages...</> : <><Cloud className="w-4 h-4 mr-2" /> Deploy to Cloudflare Pages</>}
                </Button>
              </CardContent>
            </Card>
          )}

          {cfResult && (
            <Card data-testid="cf-deploy-result">
              <CardContent className="pt-5">
                <div className="flex items-center gap-3 mb-4">
                  {cfResult.success ? <CheckCircle className="w-6 h-6 text-green-500" /> : <XCircle className="w-6 h-6 text-red-500" />}
                  <div>
                    <p className="font-medium text-slate-900">{cfResult.success ? `Deployed ${cfResult.files_deployed || ''} files successfully!` : 'Deployment failed'}</p>
                    {cfResult.pages_dev_url && <a href={cfResult.pages_dev_url} target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 hover:underline">{cfResult.pages_dev_url}</a>}
                    {cfResult.deployment_url && cfResult.deployment_url !== cfResult.pages_dev_url && <p className="text-xs text-slate-400 mt-0.5">Deployment: <a href={cfResult.deployment_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{cfResult.deployment_url}</a></p>}
                    {cfResult.error && <p className="text-sm text-red-500 mt-1">{cfResult.error}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(cfResult?.success || project?.cf_deployed) && (
            <Card data-testid="cf-custom-domain-section">
              <CardHeader>
                <CardTitle className="text-base">Custom Domain</CardTitle>
                <CardDescription>Connect your own domain to this Cloudflare Pages site.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={cfDomain}
                    onChange={(e) => setCfDomain(e.target.value)}
                    placeholder="www.example.com"
                    className="flex-1"
                    data-testid="cf-domain-input"
                  />
                  <Button
                    onClick={async () => {
                      if (!cfDomain.trim()) return;
                      setCfDomainAdding(true); setCfDomainResult(null);
                      try {
                        const res = await axios.post(`${API}/projects/${projectId}/cloudflare/add-domain`, { domain: cfDomain.trim() });
                        setCfDomainResult(res.data);
                        if (res.data.success) { toast.success(res.data.message); } else { toast.error(res.data.error); }
                      } catch (e) { toast.error("Failed to add domain"); }
                      finally { setCfDomainAdding(false); }
                    }}
                    disabled={cfDomainAdding || !cfDomain.trim()}
                    variant="outline"
                    data-testid="cf-add-domain-btn"
                  >
                    {cfDomainAdding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</> : "Add Domain"}
                  </Button>
                </div>
                {cfDomainResult?.success && cfDomainResult?.dns_instructions && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2" data-testid="cf-dns-instructions">
                    <p className="font-medium text-blue-800 text-sm">DNS Setup Required</p>
                    <pre className="text-xs text-blue-700 whitespace-pre-wrap font-mono">{cfDomainResult.dns_instructions}</pre>
                    <p className="text-xs text-blue-600">After updating your DNS records, SSL will be provisioned automatically by Cloudflare.</p>
                  </div>
                )}
                {cfDomainResult?.error && <p className="text-sm text-red-500">{cfDomainResult.error}</p>}
                {project?.cf_custom_domain && (
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/projects/${projectId}/cloudflare/domain-status`);
                      if (res.data.success) {
                        toast.success(`Domain '${res.data.domain}': ${res.data.status}`);
                      } else { toast.error(res.data.error); }
                    } catch (e) { toast.error("Failed to check domain status"); }
                  }} data-testid="cf-check-domain-btn">
                    Check Domain Status
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Backlink Network Tab */}
      {activeTab === 'backlinks' && (
        <div className="space-y-6" data-testid="backlinks-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Backlink Network</CardTitle>
                  <CardDescription>Create satellite microsites that link back to your main site. Each satellite has unique content and natural contextual backlinks.</CardDescription>
                </div>
                <Button onClick={() => setSatShowForm(!satShowForm)} className="bg-orange-500 hover:bg-orange-600" data-testid="create-satellite-btn">
                  <Plus className="w-4 h-4 mr-2" />{satShowForm ? "Cancel" : "Create Satellite Site"}
                </Button>
              </div>
            </CardHeader>
            {satShowForm && (
              <CardContent className="border-t pt-5">
                <div className="space-y-4 max-w-lg">
                  <div>
                    <Label>Content Angle</Label>
                    <select
                      value={satAngle}
                      onChange={(e) => setSatAngle(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                      data-testid="satellite-angle-select"
                    >
                      <option value="tips_blog">Tips & Guides Blog (3 pages)</option>
                      <option value="directory">Local Resource Directory (2 pages)</option>
                      <option value="education">Industry News & Education (2 pages)</option>
                      <option value="review">Review & Comparison Site (1 page)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Site Name <span className="text-slate-400 font-normal text-xs">(optional — AI will suggest one)</span></Label>
                    <Input value={satName} onChange={(e) => setSatName(e.target.value)} placeholder="e.g. Chicago Home Maintenance Tips" data-testid="satellite-name-input" />
                  </div>
                  <div>
                    <Label>Target Backlink URL <span className="text-slate-400 font-normal text-xs">(auto-detected from deploy)</span></Label>
                    <Input value={satTargetUrl} onChange={(e) => setSatTargetUrl(e.target.value)} placeholder={project?.cf_deploy_result?.pages_dev_url || "https://your-main-site.com"} data-testid="satellite-target-url" />
                  </div>
                  <Button onClick={createSatellite} disabled={satCreating} className="bg-orange-500 hover:bg-orange-600" data-testid="satellite-generate-btn">
                    {satCreating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating satellite content...</>) : "Generate Satellite Site"}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Network overview */}
          {satellites.length > 0 && (
            <div className="grid gap-3 text-sm bg-slate-50 rounded-xl p-4" data-testid="satellite-overview">
              <div className="flex items-center justify-between text-slate-600">
                <span>Main Site: <strong className="text-slate-900">{project?.business_name}</strong></span>
                <span>{satellites.length} satellite{satellites.length > 1 ? 's' : ''} &middot; {satellites.reduce((sum, s) => sum + (s.backlink_count || 0), 0)} total backlinks</span>
              </div>
            </div>
          )}

          {/* Satellite cards */}
          {satellites.map((sat) => (
            <Card key={sat.id} className={sat.status === 'deployed' ? 'border-green-200' : ''} data-testid={`satellite-card-${sat.id}`}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900 truncate">{sat.name}</h4>
                      <Badge variant={sat.status === 'deployed' ? 'default' : 'secondary'} className={`text-[10px] ${sat.status === 'deployed' ? 'bg-green-100 text-green-700' : ''}`}>{sat.status === 'deployed' ? 'Deployed' : 'Draft'}</Badge>
                      <Badge variant="outline" className="text-[10px]">{sat.angle_label || sat.content_angle}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                      <span>{sat.page_count || sat.pages?.length || 0} pages</span>
                      <span>{sat.backlink_count || 0} backlinks</span>
                      <span>Target: <a href={sat.target_url} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">{sat.target_name}</a></span>
                    </div>
                    {sat.cf_deploy_result?.pages_dev_url && (
                      <a href={sat.cf_deploy_result.pages_dev_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 block">{sat.cf_deploy_result.pages_dev_url}</a>
                    )}
                    {sat.anchor_texts?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">{sat.anchor_texts.slice(0, 4).map((a, i) => (<Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">{a}</Badge>))}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <a href={`${API}/projects/${projectId}/satellites/${sat.id}/download`} className="inline-flex"><Button variant="outline" size="sm" data-testid={`satellite-download-${sat.id}`}><Download className="w-3.5 h-3.5 mr-1.5" />ZIP</Button></a>
                    <Button variant="outline" size="sm" onClick={() => deploySatelliteCF(sat.id)} disabled={sat._deploying} data-testid={`satellite-deploy-cf-${sat.id}`}>
                      {sat._deploying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Cloud className="w-3.5 h-3.5 mr-1.5" />CF Deploy</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteSatellite(sat.id)} className="text-red-500 hover:text-red-600" data-testid={`satellite-delete-${sat.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {satellites.length === 0 && !satShowForm && (
            <div className="text-center py-12 text-slate-400">
              <Network className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No satellite sites yet. Create your first one to start building your backlink network.</p>
            </div>
          )}
        </div>
      )}

      {/* Bulk Clone Tab */}
      {activeTab === 'bulk-clone' && (
        <div className="space-y-6" data-testid="bulk-clone-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-orange-500" />Bulk Clone from CSV</CardTitle>
              <CardDescription>Clone this project for multiple cities. Upload a CSV with one row per city — all settings (template, services, phone, email) are copied from this project.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-slate-700">CSV Format (columns):</p>
                <code className="block text-xs bg-white px-3 py-2 rounded border font-mono text-slate-600">business_name, main_city, main_state, main_zip, surrounding_cities</code>
                <p className="text-xs text-slate-500">Example row: <code className="text-orange-600">Drain Fix Dallas, Dallas, TX, 75201, "Fort Worth:TX, Plano:TX, Arlington:TX"</code></p>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors flex-1">
                  <FileUp className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">{csvFile ? csvFile.name : 'Choose CSV file...'}</span>
                  <input type="file" accept=".csv" className="hidden" data-testid="csv-upload-input" onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCloneResult(null); }} />
                </label>
                <Button
                  onClick={async () => {
                    if (!csvFile) return toast.error("Please select a CSV file");
                    setCloning(true); setCloneResult(null);
                    try {
                      const fd = new FormData();
                      fd.append("file", csvFile);
                      const res = await axios.post(`${API}/projects/${projectId}/bulk-clone`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
                      setCloneResult(res.data);
                      toast.success(`${res.data.created_count} projects created!`);
                    } catch (e) { toast.error("Bulk clone failed: " + (e.response?.data?.detail || e.message)); }
                    finally { setCloning(false); }
                  }}
                  disabled={cloning || !csvFile}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="bulk-clone-btn"
                >
                  {cloning ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Cloning...</> : "Clone Projects"}
                </Button>
              </div>

              {cloneResult && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{cloneResult.created_count}</p>
                      <p className="text-xs text-green-600">Projects Created</p>
                    </div>
                    {cloneResult.error_count > 0 && (
                      <div className="flex-1 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-700">{cloneResult.error_count}</p>
                        <p className="text-xs text-red-600">Errors</p>
                      </div>
                    )}
                  </div>

                  {cloneResult.errors?.length > 0 && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                      {cloneResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
                    </div>
                  )}

                  {cloneResult.created?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-700">Created Projects</p>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="bulk-ai-generate-btn"
                          disabled={!!bulkAiJobId}
                          onClick={async () => {
                            try {
                              const ids = cloneResult.created.map(p => p.id);
                              const res = await axios.post(`${API}/bulk-ai-generate`, { project_ids: ids }, { timeout: 10000 });
                              setBulkAiJobId(res.data.job_id);
                              toast.success("AI content generation started in background!");
                            } catch (e) { toast.error("Failed to start AI generation"); }
                          }}
                        >
                          <Sparkles className="w-4 h-4 mr-1.5" />Generate AI Content for All ({cloneResult.created.length})
                        </Button>
                      </div>
                      <div className="max-h-60 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0"><tr>
                            <th className="text-left px-3 py-2 text-xs text-slate-500">Business Name</th>
                            <th className="text-left px-3 py-2 text-xs text-slate-500">City</th>
                            <th className="text-left px-3 py-2 text-xs text-slate-500">State</th>
                          </tr></thead>
                          <tbody>
                            {cloneResult.created.map((p) => (
                              <tr key={p.id} className="border-t hover:bg-slate-50">
                                <td className="px-3 py-1.5"><a href={`/admin/projects/${p.id}`} className="text-blue-600 hover:underline text-xs">{p.name}</a></td>
                                <td className="px-3 py-1.5 text-xs text-slate-600">{p.city}</td>
                                <td className="px-3 py-1.5 text-xs text-slate-600">{p.state}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Generation Progress */}
              {bulkAiJobId && (
                <BulkAiProgress jobId={bulkAiJobId} onComplete={() => { setBulkAiJobId(null); toast.success("All AI content generated!"); }} />
              )}
            </CardContent>
          </Card>

          {/* Bulk Deploy Card */}
          {cloneResult?.created?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-blue-500" />Bulk Deploy</CardTitle>
                <CardDescription>Deploy all {cloneResult.created.length} cloned projects to your chosen platform.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  {["cloudflare", "bunny"].map(p => (
                    <button key={p} onClick={() => setDeployPlatform(p)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${deployPlatform === p ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`deploy-platform-${p}`}
                    >{p === 'cloudflare' ? 'Cloudflare Pages' : 'Bunny CDN'}</button>
                  ))}
                </div>

                {deployPlatform === 'cloudflare' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>API Token</Label>
                      <Input placeholder="Cloudflare API Token" type="password" value={deployCreds.api_token} onChange={e => setDeployCreds(prev => ({...prev, api_token: e.target.value}))} data-testid="cf-api-token" />
                    </div>
                    <div>
                      <Label>Account ID</Label>
                      <Input placeholder="Cloudflare Account ID" value={deployCreds.account_id} onChange={e => setDeployCreds(prev => ({...prev, account_id: e.target.value}))} data-testid="cf-account-id" />
                    </div>
                  </div>
                )}

                {deployPlatform === 'bunny' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Storage Zone</Label>
                      <Input placeholder="Storage zone name" value={deployCreds.storage_zone} onChange={e => setDeployCreds(prev => ({...prev, storage_zone: e.target.value}))} data-testid="bunny-storage-zone" />
                    </div>
                    <div>
                      <Label>Access Key</Label>
                      <Input placeholder="Storage zone access key" type="password" value={deployCreds.access_key} onChange={e => setDeployCreds(prev => ({...prev, access_key: e.target.value}))} data-testid="bunny-access-key" />
                    </div>
                    <div>
                      <Label>Pull Zone URL</Label>
                      <Input placeholder="https://yourzone.b-cdn.net" value={deployCreds.pull_zone_url} onChange={e => setDeployCreds(prev => ({...prev, pull_zone_url: e.target.value}))} data-testid="bunny-pull-zone" />
                    </div>
                    <div>
                      <Label>Region</Label>
                      <Select value={deployCreds.region} onValueChange={v => setDeployCreds(prev => ({...prev, region: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default (Falkenstein)</SelectItem>
                          <SelectItem value="ny">New York</SelectItem>
                          <SelectItem value="la">Los Angeles</SelectItem>
                          <SelectItem value="sg">Singapore</SelectItem>
                          <SelectItem value="syd">Sydney</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Button
                  onClick={async () => {
                    setBulkDeploying(true); setBulkDeployJobId(null);
                    try {
                      const ids = cloneResult.created.map(p => p.id);
                      const creds = deployPlatform === 'cloudflare'
                        ? { api_token: deployCreds.api_token, account_id: deployCreds.account_id }
                        : { storage_zone: deployCreds.storage_zone, access_key: deployCreds.access_key, pull_zone_url: deployCreds.pull_zone_url, region: deployCreds.region };
                      const res = await axios.post(`${API}/bulk-deploy`, { project_ids: ids, platform: deployPlatform, credentials: creds }, { timeout: 10000 });
                      setBulkDeployJobId(res.data.job_id);
                      toast.success(`Deployment started for ${res.data.total} projects!`);
                    } catch (e) { toast.error("Deploy failed: " + (e.response?.data?.detail || e.message)); }
                    finally { setBulkDeploying(false); }
                  }}
                  disabled={bulkDeploying || !!bulkDeployJobId}
                  className="bg-blue-600 hover:bg-blue-700 w-full"
                  data-testid="bulk-deploy-btn"
                >
                  {bulkDeploying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Starting...</> : <>
                    <Cloud className="w-4 h-4 mr-2" />Deploy All {cloneResult.created.length} Projects to {deployPlatform === 'cloudflare' ? 'Cloudflare Pages' : 'Bunny CDN'}
                  </>}
                </Button>

                {bulkDeployJobId && (
                  <BulkDeployProgress jobId={bulkDeployJobId} onComplete={() => { setBulkDeployJobId(null); toast.success("All projects deployed!"); }} />
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};


const BulkAiProgress = ({ jobId, onComplete }) => {
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bulk-jobs/${jobId}`);
        setJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          if (res.data.status === 'completed' && onComplete) onComplete();
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId, onComplete]);

  if (!job) return <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-blue-600" /><span className="text-sm text-blue-700">Starting AI generation...</span></div>;

  const pct = job.total > 0 ? Math.round((job.completed / job.total) * 100) : 0;

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="bulk-ai-progress">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {job.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
          <span className="text-sm font-medium text-blue-900">
            {job.status === 'running' ? `Generating AI content... ${job.completed}/${job.total}` : job.status === 'completed' ? 'All AI content generated!' : 'Generation failed'}
          </span>
        </div>
        <span className="text-sm font-bold text-blue-700">{pct}%</span>
      </div>
      <div className="w-full bg-blue-200 rounded-full h-2.5">
        <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {job.current_project && job.status === 'running' && (
        <p className="text-xs text-blue-600">Currently processing: {job.current_project}</p>
      )}
      {job.errors?.length > 0 && (
        <div className="mt-2 text-xs text-red-600">
          {job.errors.slice(-3).map((e, i) => <p key={i}>{e}</p>)}
        </div>
      )}
    </div>
  );
};


const BulkDeployProgress = ({ jobId, onComplete }) => {
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bulk-jobs/${jobId}`);
        setJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          if (res.data.status === 'completed' && onComplete) onComplete();
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId, onComplete]);

  if (!job) return <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-green-600" /><span className="text-sm text-green-700">Starting deployment...</span></div>;

  const pct = job.total > 0 ? Math.round((job.completed / job.total) * 100) : 0;

  return (
    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3" data-testid="bulk-deploy-progress">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {job.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin text-green-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
          <span className="text-sm font-medium text-green-900">
            {job.status === 'running' ? `Deploying... ${job.completed}/${job.total}` : job.status === 'completed' ? 'All projects deployed!' : 'Deployment failed'}
          </span>
        </div>
        <span className="text-sm font-bold text-green-700">{pct}%</span>
      </div>
      <div className="w-full bg-green-200 rounded-full h-2.5">
        <div className="bg-green-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {job.current_project && job.status === 'running' && (
        <p className="text-xs text-green-600">Deploying: {job.current_project}</p>
      )}
      {job.results?.length > 0 && (
        <div className="max-h-40 overflow-y-auto">
          <table className="w-full text-xs">
            <tbody>
              {job.results.map((r, i) => (
                <tr key={i} className="border-t border-green-100">
                  <td className="py-1 text-green-800">{r.name}</td>
                  <td className="py-1">{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{r.url}</a> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {job.errors?.length > 0 && (
        <div className="mt-2 text-xs text-red-600">
          {job.errors.slice(-5).map((e, i) => <p key={i}>{e}</p>)}
        </div>
      )}
    </div>
  );
};
